import { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import BusinessRounded from '@mui/icons-material/BusinessRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';

/** A standard working day, for the progress bar only - nothing is enforced. */
const DAY_TARGET_HOURS = 8;

/**
 * Punch times come from the server as IST wall-clock strings with no zone
 * ("2026-09-28T09:15:27.672"). Pinning them to +05:30 keeps the timer right
 * even when the browser is set to another timezone.
 */
function istMillis(value) {
  if (!value) return null;
  const ms = Date.parse(`${String(value).slice(0, 19)}+05:30`);
  return Number.isNaN(ms) ? null : ms;
}

const pad = (n) => String(n).padStart(2, '0');

function clock(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

function hm(hours) {
  const mins = Math.round((hours || 0) * 60);
  return `${Math.floor(mins / 60)}h ${pad(mins % 60)}m`;
}

const hhmm = (value) => String(value).slice(11, 16);

/** Ticks once a second while `active`; idle otherwise so nothing re-renders. */
function useNow(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

function ModeChip({ mode }) {
  if (!mode) return null;
  const home = mode === 'HOME';
  return (
    <Chip size="small" icon={home ? <HomeRounded /> : <BusinessRounded />}
          label={home ? 'Work from home' : 'Work from office'}
          sx={{ bgcolor: home ? '#e6f5f5' : '#eef2f9', color: home ? 'secondary.main' : 'primary.main',
                '& .MuiChip-icon': { color: 'inherit' } }} />
  );
}

/** Today's punch card: choose office/home, then a live timer until punch-out. */
export default function PunchClock({ today, busy, onPunchIn, onPunchOut, onSwitchMode }) {
  const started = istMillis(today && today.punchIn);
  const running = !!started && !(today && today.punchOut);
  const now = useNow(running);

  const elapsedMs = running ? now - started : 0;
  const workedHours = running ? elapsedMs / 3600000 : (today && today.workedHours) || 0;
  const progress = Math.min(100, (workedHours / DAY_TARGET_HOURS) * 100);
  const dateLabel = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata',
  });

  return (
    <Card sx={{ mb: 3, overflow: 'hidden', position: 'relative' }}>
      {running && (
        <Box sx={{ position: 'absolute', inset: '0 0 auto 0', height: 3,
                   background: 'linear-gradient(90deg, #123871, #009da1)' }} />
      )}
      <CardContent sx={{ p: { xs: 2.5, sm: 3 }, '&:last-child': { pb: { xs: 2.5, sm: 3 } } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}
               sx={{ justifyContent: 'space-between', alignItems: { md: 'center' } }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Today · {dateLabel}
              </Typography>
              {running && (
                <Chip size="small" label="Working" color="success" variant="outlined"
                      icon={<Box component="span" className="live-dot" />}
                      sx={{ height: 22, '& .MuiChip-icon': { ml: '8px' } }} />
              )}
              {today && today.punchOut && (
                <Chip size="small" label="Day closed" icon={<CheckCircleRounded />} sx={{ height: 22 }} />
              )}
            </Stack>

            {!started && (
              <>
                <Typography variant="h5" sx={{ mt: 0.5 }}>You haven't punched in yet</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Where are you working today?
                </Typography>
              </>
            )}

            {started && (
              <>
                <Typography component="div" aria-live="off"
                            aria-label={running ? `Time worked so far ${hm(workedHours)}` : `Worked ${hm(workedHours)}`}
                            sx={{ fontSize: { xs: 40, sm: 48 }, fontWeight: 700, letterSpacing: '-0.02em',
                                  lineHeight: 1.1, fontVariantNumeric: 'tabular-nums',
                                  color: running ? 'primary.main' : 'text.primary' }}>
                  {running ? clock(elapsedMs) : hm(workedHours)}
                </Typography>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    In at <strong>{hhmm(today.punchIn)}</strong>
                    {today.punchOut && <> · Out at <strong>{hhmm(today.punchOut)}</strong></>}
                  </Typography>
                  <ModeChip mode={today.workMode} />
                </Stack>
                <Box sx={{ mt: 2, maxWidth: 420 }}>
                  <LinearProgress variant="determinate" value={progress}
                                  color={progress >= 100 ? 'success' : 'secondary'}
                                  sx={{ height: 8, borderRadius: 4, bgcolor: '#eef1f5' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {progress >= 100
                      ? `${DAY_TARGET_HOURS}h day complete`
                      : `${hm(Math.max(0, DAY_TARGET_HOURS - workedHours))} to go of ${DAY_TARGET_HOURS}h`}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          <Stack spacing={1} sx={{ alignItems: { xs: 'stretch', md: 'flex-end' }, flexShrink: 0 }}>
            {!started ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" size="large" disabled={busy} startIcon={<BusinessRounded />}
                        onClick={() => onPunchIn('OFFICE')}>
                  Punch in · Office
                </Button>
                <Button variant="contained" color="secondary" size="large" disabled={busy}
                        startIcon={<HomeRounded />} onClick={() => onPunchIn('HOME')}>
                  Punch in · Home
                </Button>
              </Stack>
            ) : running ? (
              <Button variant="outlined" size="large" disabled={busy} startIcon={<LogoutRounded />}
                      onClick={onPunchOut} sx={{ minWidth: 170 }}>
                Punch out
              </Button>
            ) : null}
            {started && (
              <Button size="small" disabled={busy} color="secondary"
                      onClick={() => onSwitchMode(today.workMode === 'HOME' ? 'OFFICE' : 'HOME')}>
                {today.workMode
                  ? `Wrong? Switch to ${today.workMode === 'HOME' ? 'office' : 'home'}`
                  : 'Set office / home for today'}
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
